import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  FileText, Search, Filter, Clock, CheckCircle, XCircle, 
  AlertCircle, ChevronRight, User, Calendar, Eye
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminQuoteRequests = () => {
  const { isMainAdmin } = useAuth();
  const isSuperAdmin = isMainAdmin();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get('/admin/quote-process/requests');
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', icon: XCircle }
    };
    const style = styles[status] || styles.in_progress;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 ${style.bg} ${style.text} px-3 py-1 rounded-full text-xs font-medium`}>
        <Icon className="w-3.5 h-3.5" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const filteredRequests = requests.filter(req => {
    const matchesFilter = filter === 'all' || 
      (filter === 'pending' && req.pending_steps > 0) ||
      (filter === 'completed' && req.status === 'completed') ||
      (filter === 'in_progress' && req.status === 'in_progress');
    
    const matchesSearch = !searchTerm ||
      req.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Quote Requests</h1>
          <p className="text-gray-500 text-sm mt-1">Review and manage user quote submissions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Total Requests</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{requests.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Pending Review</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: BRAND_ORANGE }}>
              {requests.filter(r => r.pending_steps > 0).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">In Progress</p>
            <p className="text-2xl font-semibold text-blue-600 mt-1">
              {requests.filter(r => r.status === 'in_progress').length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-semibold text-green-600 mt-1">
              {requests.filter(r => r.status === 'completed').length}
            </p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6 flex flex-col md:flex-row gap-4">
          <FilterInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by user name, email, or request ID..."
            className="flex-1"
            data-testid="search-input"
          />
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <FilterSelect
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Requests' },
                { value: 'pending', label: 'Pending Review' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' }
              ]}
              data-testid="status-filter"
            />
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {filteredRequests.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No quote requests found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50/80 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Request ID</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Product</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                  {isSuperAdmin && (
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Company</th>
                  )}
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Step</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-gray-600">#{request.id?.slice(-8)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700 font-medium">{request.product_name || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{request.user_name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{request.user_email}</p>
                        </div>
                      </div>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{request.company_name || '-'}</span>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${BRAND_BLUE}15`, color: BRAND_BLUE }}>
                          Step {request.current_step || 1}
                        </span>
                        <span className="text-xs text-gray-400">({request.progress})</span>
                        {request.pending_steps > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${BRAND_ORANGE}20`, color: BRAND_ORANGE }}>
                            <AlertCircle className="w-3 h-3" />
                            {request.pending_steps} pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(request.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/admin/quote-requests/${request.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition"
                        style={{ color: BRAND_BLUE }}
                        data-testid={`view-request-${request.id}`}
                        title="Review Request"
                      >
                        <Eye className="w-5 h-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminQuoteRequests;
